/**
 * TradeStack - Job Detail Page
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './JobDetail.css';

function JobDetail() {
  const { id } = useParams();
  const { config, formatCurrency, formatDate } = useConfig();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadJob = async () => {
    try {
      const response = await api.getJob(id);
      setJob(response.data.job);
    } catch (err) {
      setError('Failed to load job');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.updateJob(id, { status: newStatus });
      setJob({ ...job, status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = config?.jobStatuses?.find(s => s.id === status);
    return (
      <span className={`status-badge status-${status}`}>
        {statusConfig?.name || statusConfig?.label || status}
      </span>
    );
  };

  if (loading) {
    return <div className="job-detail-page"><div className="loading-state">Loading...</div></div>;
  }

  if (error || !job) {
    return (
      <div className="job-detail-page">
        <div className="error-state">
          {error || 'Job not found'}
          <Link to="/jobs" className="btn btn-secondary">Back to Jobs</Link>
        </div>
      </div>
    );
  }

  const statuses = config?.jobStatuses || [
    { id: 'lead', name: 'Lead' },
    { id: 'quoted', name: 'Quoted' },
    { id: 'scheduled', name: 'Scheduled' },
    { id: 'in_progress', name: 'In Progress' },
    { id: 'completed', name: 'Completed' }
  ];

  return (
    <div className="job-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <Link to="/jobs" className="back-link">&larr; Back to Jobs</Link>
          <h1>{job.title}</h1>
          <div className="header-meta">
            {getStatusBadge(job.status)}
            {job.customer_name && (
              <Link to={`/customers/${job.customer_id}`} className="customer-link">
                {job.customer_name}
              </Link>
            )}
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/jobs/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <Link to={`/estimates/new?jobId=${id}`} className="btn btn-secondary">Create Estimate</Link>
          <Link to={`/invoices/new?jobId=${id}`} className="btn btn-primary">Create Invoice</Link>
        </div>
      </div>

      {/* Status Flow */}
      <div className="status-flow">
        {statuses.map((status, index) => (
          <button
            key={status.id}
            className={`status-step ${job.status === status.id ? 'current' : ''} ${
              statuses.findIndex(s => s.id === job.status) > index ? 'completed' : ''
            }`}
            onClick={() => updateStatus(status.id)}
          >
            <span className="step-indicator">{index + 1}</span>
            <span className="step-label">{status.name || status.label}</span>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'phases' ? 'active' : ''}`}
          onClick={() => setActiveTab('phases')}
        >
          Phases
        </button>
        <button
          className={`tab-btn ${activeTab === 'financials' ? 'active' : ''}`}
          onClick={() => setActiveTab('financials')}
        >
          Financials
        </button>
        <button
          className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
        <button
          className={`tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          Photos
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="info-grid">
              <div className="info-card">
                <h3>Job Details</h3>
                <div className="info-rows">
                  {job.description && (
                    <div className="info-row">
                      <span className="info-label">Description</span>
                      <span className="info-value">{job.description}</span>
                    </div>
                  )}
                  {job.address_line1 && (
                    <div className="info-row">
                      <span className="info-label">Address</span>
                      <span className="info-value">{job.address_line1}</span>
                    </div>
                  )}
                  {job.scheduled_start && (
                    <div className="info-row">
                      <span className="info-label">Scheduled Date</span>
                      <span className="info-value">{formatDate(job.scheduled_start)}</span>
                    </div>
                  )}
                  {job.estimated_hours && (
                    <div className="info-row">
                      <span className="info-label">Estimated Hours</span>
                      <span className="info-value">{job.estimated_hours} hours</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="info-card">
                <h3>Financials Summary</h3>
                <div className="financial-summary">
                  <div className="financial-item">
                    <span className="financial-label">Estimated Value</span>
                    <span className="financial-value">{formatCurrency(job.estimated_value || 0)}</span>
                  </div>
                  <div className="financial-item">
                    <span className="financial-label">Invoiced</span>
                    <span className="financial-value">{formatCurrency(job.total_invoiced || 0)}</span>
                  </div>
                  <div className="financial-item">
                    <span className="financial-label">Paid</span>
                    <span className="financial-value positive">{formatCurrency(job.total_paid || 0)}</span>
                  </div>
                  <div className="financial-item">
                    <span className="financial-label">Balance Due</span>
                    <span className="financial-value">{formatCurrency((job.total_invoiced || 0) - (job.total_paid || 0))}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress */}
            {job.progress_percent !== undefined && (
              <div className="progress-section">
                <h3>Progress: {job.progress_percent}%</h3>
                <div className="large-progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${job.progress_percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'phases' && (
          <div className="phases-content">
            <div className="section-header">
              <h3>Job Phases</h3>
              <button className="btn btn-secondary">+ Add Phase</button>
            </div>
            {(!job.phases || job.phases.length === 0) ? (
              <div className="empty-tab">
                <p>No phases added yet</p>
              </div>
            ) : (
              <div className="phases-list">
                {job.phases.map((phase, index) => (
                  <div key={phase.id} className="phase-card">
                    <div className="phase-order">{index + 1}</div>
                    <div className="phase-content">
                      <h4>{phase.name}</h4>
                      <p>{phase.description}</p>
                    </div>
                    <div className="phase-status">
                      <span className={`status-badge status-${phase.status}`}>
                        {phase.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'financials' && (
          <div className="financials-content">
            <div className="section-header">
              <h3>Estimates & Invoices</h3>
            </div>
            <div className="financials-grid">
              <div className="financial-section">
                <h4>Estimates</h4>
                {(!job.estimates || job.estimates.length === 0) ? (
                  <div className="empty-section">
                    <p>No estimates yet</p>
                    <Link to={`/estimates/new?jobId=${id}`} className="btn btn-secondary btn-sm">
                      Create Estimate
                    </Link>
                  </div>
                ) : (
                  <div className="document-list">
                    {job.estimates.map(est => (
                      <Link to={`/estimates/${est.id}`} key={est.id} className="document-item">
                        <span className="doc-number">{est.estimate_number}</span>
                        <span className="doc-amount">{formatCurrency(est.total)}</span>
                        <span className={`status-badge status-${est.status}`}>{est.status}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="financial-section">
                <h4>Invoices</h4>
                {(!job.invoices || job.invoices.length === 0) ? (
                  <div className="empty-section">
                    <p>No invoices yet</p>
                    <Link to={`/invoices/new?jobId=${id}`} className="btn btn-secondary btn-sm">
                      Create Invoice
                    </Link>
                  </div>
                ) : (
                  <div className="document-list">
                    {job.invoices.map(inv => (
                      <Link to={`/invoices/${inv.id}`} key={inv.id} className="document-item">
                        <span className="doc-number">{inv.invoice_number}</span>
                        <span className="doc-amount">{formatCurrency(inv.total)}</span>
                        <span className={`status-badge status-${inv.status}`}>{inv.status}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="notes-content">
            <div className="section-header">
              <h3>Notes</h3>
              <button className="btn btn-secondary">+ Add Note</button>
            </div>
            {(!job.notes || job.notes.length === 0) ? (
              <div className="empty-tab">
                <p>No notes added yet</p>
              </div>
            ) : (
              <div className="notes-list">
                {job.notes.map(note => (
                  <div key={note.id} className="note-card">
                    <div className="note-header">
                      <span className="note-author">{note.author_name}</span>
                      <span className="note-date">{formatDate(note.created_at)}</span>
                    </div>
                    <p className="note-content">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="photos-content">
            <div className="section-header">
              <h3>Photos</h3>
              <button className="btn btn-secondary">+ Upload Photos</button>
            </div>
            {(!job.photos || job.photos.length === 0) ? (
              <div className="empty-tab">
                <p>No photos uploaded yet</p>
              </div>
            ) : (
              <div className="photos-grid">
                {job.photos.map(photo => (
                  <div key={photo.id} className="photo-card">
                    <img src={photo.url} alt={photo.caption || 'Job photo'} />
                    {photo.caption && <span className="photo-caption">{photo.caption}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default JobDetail;
